import type { AnimeServer } from './types.ts';
import { AnimeServerUnavailableError, extractStream, proxyUrl } from './AnimeExtractor.ts';
class VerificationRequired extends Error {}

async function isPlayableMedia(url: string, hls: boolean): Promise<boolean | undefined> {
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const response = await fetch(url, {headers: {Range: 'bytes=0-1023'}, signal: AbortSignal.timeout(28000)});
    if(response.status>=500){await response.body?.cancel();return undefined;}
    if (!response.ok || !response.body) {
      if (response.status === 403 && /hotlink_detected|captcha_required/.test(await response.text())) throw new VerificationRequired('PDrain requiere verificación en su sitio.');
      return false;
    }
    reader = response.body.getReader();
    const {value} = await reader.read();
    if (!value?.length) return false;
    const head = new TextDecoder().decode(value.slice(0, 256));
    if (hls) return head.trimStart().startsWith('#EXTM3U');
    return head.slice(4, 12).includes('ftyp') || /^video\//i.test(response.headers.get('content-type') || '');
  } catch (error) { if(error instanceof VerificationRequired)throw error;return undefined; }
  finally { await reader?.cancel().catch(() => {}); }
}

/** Resolve supported file hosts consistently for every installed anime connector. */
export async function resolveAnimeServers(servers: AnimeServer[]): Promise<AnimeServer[]> {
  const result = [...servers];
  let cursor = 0;
  async function worker() {
    while (cursor < result.length) {
      const index = cursor++, server = result[index];
      if (server.id.startsWith('dl:')) continue;
      if (server.delivery !== 'embed') {
        if (/^https?:/.test(server.url)) result[index] = {...server, rawUrl: server.rawUrl || server.url, url: proxyUrl(server.url, server.referer), isNative: true};
        continue;
      }
      const url = new URL(server.url);
      const supported = /mp4upload|voe|streamwish|strwish|wishembed|mixdrop|mxdrop|luluvdo|lulustream|vidhide|callistanise|pixeldrain/.test(url.hostname)
        || /^byse[a-z0-9-]*\.com$/.test(url.hostname) || url.hostname === 'animeav1.uns.bio'
        || (url.hostname === 'ok.ru' && url.pathname.startsWith('/videoembed/'))
        || (url.hostname === 'mytsumi.com' && url.pathname.startsWith('/multiplayer/play2026/'))
        || (url.hostname === 'jkanime.net' && url.pathname.startsWith('/jkplayer/'));
      if (!supported) continue;
      try {
        const stream = await extractStream(server.name, server.url);
        const media = stream ? new URL(stream.rawUrl) : undefined;
        const mp4UploadFile = !!media && /^[a-z0-9-]+\.mp4upload\.com$/.test(media.hostname) && /^\/d\/[a-z0-9_/-]+\.mp4$/i.test(media.pathname);
        // A timeout/5xx says nothing about the extracted delivery type. Only a
        // definite non-media response should send the user back to an embed.
        if (stream && (mp4UploadFile || await isPlayableMedia(stream.url, stream.isM3U8)!==false)) result[index] = {...server, url: stream.url, rawUrl: stream.rawUrl, delivery: stream.isM3U8 ? 'hls' : 'direct', isNative: true, quality: server.quality || stream.quality, downloadUrl: stream.downloadUrl, referer: stream.headers?.Referer || server.referer};
      } catch (error) {
        if (error instanceof AnimeServerUnavailableError) result[index] = {...server, unavailableReason: error.message};
        if (error instanceof VerificationRequired) result[index] = {...server, playbackNote: error.message};
      }
    }
  }
  await Promise.all([worker(), worker(), worker()]);
  return result.sort((a, b) => Number(!!b.isNative) - Number(!!a.isNative)
    || (a.isNative && b.isNative ? Number(b.delivery === 'direct') - Number(a.delivery === 'direct') : 0));
}
